import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faList,
  faThLarge,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faTrash,
  faEye,
  faFilter,
  faBan,
  faVirus,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateOnly, formatTimeOnly } from "../../utils/DateTimeUtil";
import LoadingSpinner from "../../common/LoadingSpinner";
import EmptyStatePanel from "../common/EmptyStatePanel";
import "../../css/Common.css";
import "./css/ImageAnalysisResults.css";

/**
 * Component to display list of image analyses
 */
const ImageAnalysisResults = ({
  analyses,
  loading,
  selectedAnalysis,
  onViewDetails,
  onDelete,
  onFilterChange,
  onRefresh,
}) => {
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  /**
   * Handle filter change
   */
  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  // NOTE: We DO show security-failed images in the results list
  // The actual image is deleted, but we show the analysis record with error message
  // This gives users important feedback: "Your upload failed because..."
  // No filtering needed - show all analyses including FAIL verdicts
  const safeAnalyses = analyses;

  /**
   * Sort analyses
   */
  const getSortedAnalyses = () => {
    // Use safeAnalyses instead of all analyses
    const sorted = [...safeAnalyses];

    switch (sortBy) {
      case "date-desc":
        return sorted.sort(
          (a, b) => new Date(b.analyzedAt) - new Date(a.analyzedAt),
        );
      case "date-asc":
        return sorted.sort(
          (a, b) => new Date(a.analyzedAt) - new Date(b.analyzedAt),
        );
      case "confidence-desc":
        return sorted.sort((a, b) => b.confidence - a.confidence);
      case "confidence-asc":
        return sorted.sort((a, b) => a.confidence - b.confidence);
      case "filename":
        return sorted.sort((a, b) => a.fileName.localeCompare(b.fileName));
      default:
        return sorted;
    }
  };

  /**
   * Format scam type
   */
  const formatScamType = (scamType) => {
    if (!scamType) return "Unknown";
    return scamType
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  /**
   * Format date - using centralized DateTimeUtil
   */
  const formatDate = (dateString) => {
    return formatDateOnly(dateString, "short");
  };

  /**
   * Format time - using centralized DateTimeUtil
   */
  const formatTime = (dateString) => {
    return formatTimeOnly(dateString, true);
  };

  /**
   * Get scam type icon
   */
  const getScamTypeIcon = (scamType) => {
    const icons = {
      PHISHING: "🎣",
      QR_CODE_SCAM: "📱",
      FAKE_INVOICE: "🧾",
      IMPERSONATION: "🎭",
      LOTTERY_SCAM: "🎰",
      ROMANCE_SCAM: "💔",
      INVESTMENT_SCAM: "💰",
      FINANCIAL_FRAUD: "💳",
      TECH_SUPPORT_SCAM: "💻",
      MALWARE: "🦠",
      SOCIAL_ENGINEERING: "🧠",
      UNKNOWN: "❓",
    };
    return icons[scamType] || "⚠️";
  };

  const sortedAnalyses = getSortedAnalyses();

  // Pagination calculations
  const totalPages = Math.ceil(sortedAnalyses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAnalyses = sortedAnalyses.slice(startIndex, endIndex);

  // Pagination functions
  const goToPage = (page) => setCurrentPage(page);
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const goToLastPage = () => setCurrentPage(totalPages);

  // Reset to page 1 when analyses change
  useEffect(() => {
    setCurrentPage(1);
  }, [analyses]);

  return (
    <div className="results-container">
      {/* Loading State */}
      {loading && <LoadingSpinner message="Loading analyses..." />}

      {/* Empty State */}
      {!loading && sortedAnalyses.length === 0 && (
        <EmptyStatePanel
          icon={faImage}
          title="No analyses found"
          description="Upload your first image to get started!"
          className="empty-state"
        />
      )}

      {/* Results Grid/List */}
      {!loading && sortedAnalyses.length > 0 && (
        <>
          <div className={`results-${viewMode}`}>
            {paginatedAnalyses.map((analysis) => {
              // Extract scam analysis from nested analysisResult
              const analysisResult = analysis.analysisResult || {};

              // Use the backend's scam determination directly
              // The confidence score represents how confident the AI is in this determination
              // (not the probability that it's a scam)
              const confidenceValue = analysisResult.confidence || 0;
              const isScam = analysisResult.isScam === true;

              // Check for security failure
              const securityVerdict =
                analysis.securityVerdict || analysisResult.securityVerdict;
              const isSecurityFailure = securityVerdict === "FAIL";
              const hasMalware =
                analysis.hasMalware || analysisResult.hasMalware || false;
              const processingError =
                analysis.processingError ||
                analysisResult.processingError ||
                "";

              // Determine security failure type and message
              let securityFailureLabel = "Blocked";
              if (isSecurityFailure) {
                if (hasMalware) {
                  securityFailureLabel = "Virus";
                } else if (
                  processingError.toLowerCase().includes("inappropriate") ||
                  processingError.toLowerCase().includes("content")
                ) {
                  securityFailureLabel = "Unsafe";
                } else if (processingError.toLowerCase().includes("virus")) {
                  securityFailureLabel = "Virus";
                } else if (
                  processingError.toLowerCase().includes("file type") ||
                  processingError.toLowerCase().includes("invalid")
                ) {
                  securityFailureLabel = "Invalid";
                } else {
                  securityFailureLabel = "Blocked";
                }
              }

              const isSelected =
                selectedAnalysis &&
                selectedAnalysis.uploadId === analysis.uploadId;

              // Determine card class based on status
              const cardClass = isSecurityFailure
                ? "security-failed"
                : isScam
                  ? "scam"
                  : "safe";

              return (
                <div
                  key={analysis.uploadId}
                  className={`analysis-card ${cardClass} ${
                    isSelected ? "active" : ""
                  }`}
                  onClick={() => onViewDetails(analysis)}
                >
                  {/* Image Thumbnail - show placeholder for security failures */}
                  <div className="card-thumbnail">
                    {isSecurityFailure ? (
                      <div className="placeholder-thumbnail security-blocked">
                        <FontAwesomeIcon icon={hasMalware ? faVirus : faBan} />
                        <span className="blocked-label">Blocked</span>
                      </div>
                    ) : analysis.imageUrl ? (
                      <img src={analysis.imageUrl} alt="Analysis" />
                    ) : (
                      <div className="placeholder-thumbnail">
                        <FontAwesomeIcon icon={faImage} />
                      </div>
                    )}
                  </div>

                  {/* Status Column */}
                  <div className="card-status-column">
                    <span
                      className={`status-badge ${
                        isSecurityFailure
                          ? securityFailureLabel.toLowerCase()
                          : isScam
                            ? "scam"
                            : "safe"
                      }`}
                    >
                      {isSecurityFailure ? (
                        <>
                          <FontAwesomeIcon
                            icon={hasMalware ? faVirus : faBan}
                          />{" "}
                          {securityFailureLabel}
                        </>
                      ) : isScam ? (
                        <>
                          <FontAwesomeIcon icon={faExclamationTriangle} /> Scam
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} /> Safe
                        </>
                      )}
                    </span>
                    {!isSecurityFailure && (
                      <span className="confidence-value">
                        {(confidenceValue * 100).toFixed(0)}% sure
                      </span>
                    )}
                    {isSecurityFailure && (
                      <span className="confidence-value security-note">
                        Security check failed
                      </span>
                    )}
                  </div>

                  {/* Date/Time Column */}
                  <div className="card-datetime-column">
                    <span className="date-value">
                      {formatDate(analysis.analyzedAt)}
                    </span>
                    <span className="time-value">
                      {formatTime(analysis.analyzedAt)}
                    </span>
                  </div>

                  {/* Card Actions */}
                  <div className="card-actions">
                    <button
                      className="common-btn-danger btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(analysis.uploadId);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                >
                  « First
                </button>

                <button
                  className="pagination-btn"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  ‹ Previous
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        className={`pagination-page ${
                          page === currentPage ? "active" : ""
                        }`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <button
                  className="pagination-btn"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next ›
                </button>

                <button
                  className="pagination-btn"
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                >
                  Last »
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageAnalysisResults;
